import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import GlobalHeader from "@/components/GlobalHeader";
import AppFooter from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import {
  Calendar, Users, Music, MessageSquare, Image, Gift,
  MapPin, CheckSquare, Camera, Video, Gamepad2, Clock,
  Star, BookOpen, Shirt, CalendarClock, Car, Trophy
} from "lucide-react";

export default function Features({ onLogin }) {
  const { language } = useLanguage();

  // Organisieren & Planen (Slimmed down)
  const organisationFeatures = [
    { icon: MapPin, key: "location", color: "from-green-100 to-green-50" },
    { icon: Users, key: "seating_plan", color: "from-yellow-100 to-yellow-50" },
    { icon: Shirt, key: "dresscode", color: "from-purple-100 to-purple-50" },
    { icon: CalendarClock, key: "schedule", color: "from-sky-100 to-sky-50" },
    { icon: CheckSquare, key: "tasks", color: "from-indigo-100 to-indigo-50" },
    { icon: Gift, key: "wishlist", color: "from-red-100 to-red-50" },
    { icon: Car, key: "carpool", color: "from-blue-100 to-blue-50" },
  ];

  // Mitmachen & Fun Zone
  const funFeatures = [
    { icon: BookOpen, key: "guestbook", color: "from-orange-100 to-orange-50" },
    { icon: MessageSquare, key: "chat", color: "from-green-100 to-green-50" },
    { icon: Camera, key: "selfie_game", color: "from-pink-100 to-pink-50" },
    { icon: Trophy, key: "leaderboard", color: "from-yellow-100 to-yellow-50" },
  ];

  // Medien
  const mediaFeatures = [
    { icon: Image, key: "photo_upload", color: "from-pink-100 to-pink-50" },
    { icon: Video, key: "video_upload", color: "from-blue-100 to-blue-50" },
    { icon: Music, key: "music_upload", color: "from-purple-100 to-purple-50" },
  ];

  const getFeatureName = (key) => {
    const names = {
      de: {
        location: "Standort/Location",
        seating_plan: "Sitzplan",
        dresscode: "Dresscode",
        schedule: "Zeitplan",
        tasks: "Aufgabenliste",
        wishlist: "Geschenk-Wünsche",
        carpool: "Mitfahrgelegenheit",
        guestbook: "Gästebuch",
        chat: "Chat",
        selfie_game: "Selfie Spiel",
        leaderboard: "Rangliste",
        photo_upload: "Bilder-Upload",
        video_upload: "Video-Upload",
        music_upload: "Musik-Upload",
      },
      en: {
        location: "Location",
        seating_plan: "Seating Plan",
        dresscode: "Dress Code",
        schedule: "Schedule",
        tasks: "Task List",
        wishlist: "Wishlist",
        carpool: "Ride Sharing",
        guestbook: "Guestbook",
        chat: "Chat",
        selfie_game: "Selfie Game",
        leaderboard: "Leaderboard",
        photo_upload: "Photo Upload",
        video_upload: "Video Upload",
        music_upload: "Music Upload",
      }
    };
    return names[language]?.[key] || key;
  };

  const getFeatureDescription = (key) => {
    const descriptions = {
      de: {
        location: "Teile den Veranstaltungsort und wichtige Anreise-Infos.",
        seating_plan: "Visualisiere die Sitzordnung für deine Gäste.",
        dresscode: "Informiere deine Gäste über den gewünschten Kleidungsstil.",
        schedule: "Detaillierter zeitlicher Ablauf für alle Beteiligten.",
        tasks: "Verwalte alle To-Dos an einem zentralen Ort.",
        wishlist: "Zeige deinen Gästen, womit sie dir eine Freude machen können.",
        carpool: "Hilf deinen Gästen dabei, Fahrgemeinschaften zu bilden.",
        guestbook: "Digitale Glückwünsche und Nachrichten deiner Gäste.",
        chat: "Echtzeit-Kommunikation für alle Teilnehmer.",
        selfie_game: "Interaktive Foto-Aufgaben für mehr Spaß auf deinem Event.",
        leaderboard: "Sammle Punkte und kröne den aktivsten Gast.",
        photo_upload: "Alle Gäste können ihre Fotos direkt hochladen.",
        video_upload: "Sammle bewegte Bilder und Videos deiner Feier.",
        music_upload: "Erstelle eine gemeinsame Playlist mit deinen Gästen.",
      },
      en: {
        location: "Share the event venue and travel information.",
        seating_plan: "Visualize the seating arrangement for your guests.",
        dresscode: "Inform guests about the desired style.",
        schedule: "Detailed timing for everyone involved.",
        tasks: "Manage all to-dos in one central place.",
        wishlist: "Show guests what would make you happy.",
        carpool: "Help your guests organize carpools.",
        guestbook: "Digital greetings and messages from your guests.",
        chat: "Real-time communication for all participants.",
        selfie_game: "Interactive photo challenges for more fun.",
        leaderboard: "Collect points and crown the most active guest.",
        photo_upload: "All guests can upload their photos directly.",
        video_upload: "Collect moving images and videos of your party.",
        music_upload: "Create a shared playlist with your guests.",
      }
    };
    return descriptions[language]?.[key] || "";
  };

  const renderFeatureCard = (feature, idx) => (
    <motion.div
      key={feature.key}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.03 }}
      className={`p-6 rounded-2xl bg-gradient-to-br ${feature.color} border border-border/20 hover:shadow-lg transition-all`}
    >
      <div className="flex flex-col items-center text-center gap-3">
        <div className="p-3 rounded-full bg-white/80 shadow-sm">
          <feature.icon className="w-6 h-6 text-foreground" />
        </div>
        <h3 className="font-semibold text-base">
          {getFeatureName(feature.key)}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {getFeatureDescription(feature.key)}
        </p>
      </div>
    </motion.div>
  );

  const ctaContent = language === 'de' ? {
    title: "Bereit für dein Event?",
    subtitle: "Starte jetzt und nutze alle Features für dein perfektes Event",
    button: "Jetzt kostenlos starten",
    hint: "✨ Wir arbeiten stetig an neuen, innovativen Funktionen. Freu dich auf regelmäßige Updates!"
  } : {
    title: "Ready for your event?",
    subtitle: "Get started now and use all features for your perfect event",
    button: "Start for free now",
    hint: "✨ We are constantly working on new, innovative features. Stay tuned for regular updates!"
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Global Header */}
      <GlobalHeader onLogin={onLogin} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              {language === 'de' ? 'Alle Features im Überblick' : 'All Features at a Glance'}
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {language === 'de'
                ? 'Entdecke die umfangreichen Funktionen von mokiplaner und plane dein perfektes Event'
                : 'Discover the comprehensive features of mokiplaner and plan your perfect event'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Organisation Features */}
      <section className="pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-2xl font-serif mb-6 flex items-center gap-3"
          >
            <CheckSquare className="w-6 h-6 text-primary" />
            {language === 'de' ? 'Organisieren & Planen' : 'Organize & Plan'}
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
            {organisationFeatures.map(renderFeatureCard)}
          </div>
        </div>
      </section>

      {/* Fun Zone Features */}
      <section className="pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-2xl font-serif mb-6 flex items-center gap-3"
          >
            <Gamepad2 className="w-6 h-6 text-pink-500" />
            {language === 'de' ? 'Mitmachen & Fun Zone' : 'Participate & Fun Zone'}
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {funFeatures.map(renderFeatureCard)}
          </div>
        </div>
      </section>

      {/* Media Features */}
      <section className="pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-2xl font-serif mb-6 flex items-center gap-3"
          >
            <Image className="w-6 h-6 text-blue-500" />
            {language === 'de' ? 'Medien' : 'Media'}
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mediaFeatures.map(renderFeatureCard)}
          </div>
        </div>
      </section>

      {/* CTA & Coming Soon Section */}
      <section className="pb-20 pt-10 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl p-12 border border-primary/20"
          >
            <h2 className="font-serif text-3xl mb-4">
              {ctaContent.title}
            </h2>
            <p className="text-muted-foreground mb-8">
              {ctaContent.subtitle}
            </p>
            <Button
              onClick={onLogin}
              size="lg"
              className="rounded-full px-12 mb-10 h-14 text-lg"
            >
              {ctaContent.button}
            </Button>

            <div className="pt-10 border-t border-primary/10">
              <div className="flex items-center justify-center gap-3 text-primary">
                <Star className="w-5 h-5 fill-current animate-pulse" />
                <p className="font-bold text-lg tracking-tight">
                  {ctaContent.hint}
                </p>
                <Star className="w-5 h-5 fill-current animate-pulse" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
}
